import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import Slider from 'react-slick';
import PropTypes from 'prop-types';
import Image from '../../base_image/component';
import { getDevice } from '@utils';

import './styles/styles.scss';

const newLocal = ReactDOM.findDOMNode;
/**
 * Product carrousel component
 *
 * This wil render a slick slider with custom navigation for the 3D book flip
 *
 */
class ProductCarrousel extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showBack: false,
      showSide: false,
      productThickness: 60,
    };

    this.updateProductThickness = this.updateProductThickness.bind(this);
    this.toggleFlip = this.toggleFlip.bind(this);
    this.toggleSide = this.toggleSide.bind(this);
    this.customNavClick = this.customNavClick.bind(this);
    this.customNavHover  = this.customNavHover.bind(this);

    this.bookWrapper = React.createRef();
    this.pdpslider = React.createRef();
    this.book = React.createRef();
    this.customNav = React.createRef();
  }

  componentDidMount() {
    // Update book thickness on screen resize
    window.addEventListener('resize', this.updateProductThickness);
    // Update book thickness on screen resize
    window.addEventListener('load', this.updateProductThickness);
    if (this.customNav.current) {
      this.customNav.current.addEventListener('click', this.customNavClick);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateProductThickness);
    window.removeEventListener('load', this.updateProductThickness);
    if (this.customNav.current) {
      this.customNav.current.removeEventListener('click', this.customNavClick);
    }
  }

  // Updates the rendering of book thickness based on actual book width
  updateProductThickness() {
    const el = newLocal(this.bookWrapper.current);
    if (!el) {
      return;
    }
    // Get the rendered book width
    const renderedProductWidth = el.getBoundingClientRect().width;
    // Calculate ratio between physical  product width and physical product thickness
    const ratio = this.props.productWidth / this.props.productHeight;
    // Use calculated ratio of the physical dimensions to calculate proportional rendered book thickness
    const productThickness = Math.floor(renderedProductWidth / ratio);
    // To be sure. Minimum book thickness of 10 px
    if (!productThickness || productThickness < 10) {
      return;
    }
    // Update book thickness in the state
    this.setState({
      productThickness,
    });
  }

  customNavClick(e) {
    const { isMobile, isTablet } = getDevice();
    const target = e.target.closest('.pdp-image-thumb');
    if (!target || target.classList.contains('active')) {
      return false;
    }
    this.toggleFlip();
    if (!isMobile || !isTablet) {
      this.toggleSide();
    }
  }

  customNavHover(e) {
    const target = e.target.closest('.pdp-image-thumb');
    if (!target || target.classList.contains('active')) {
      return false;
    }
    this.toggleSide();
  }

  toggleFlip() {
    const { isMobile, isTablet } = getDevice();
    this.setState({
      showBack: !this.state.showBack,
      showSide: (isMobile || isTablet) ? false : this.state.showSide,
    });
  }

  toggleSide() {
    this.setState({
      showSide: !this.state.showSide,
    });
  }

  bookFlipNav = (showPreview = true) => {
    const { alt, frontImage, backImage, previewComponent, fallbackImage } = this.props;
    const isBack = this.state.showBack ? 'active' : '';
    const isFront = !this.state.showBack ? 'active' : '';
    return (
      <div className="flex-dots">
        <span
          id="bookflip-nav"
          ref={this.customNav}
          onMouseOver={this.customNavHover}
          onMouseOut={this.customNavHover}
        >
          <div className={`pdp-image-thumb front-image ${isFront}`} >
            <a>
              <Image src={frontImage} alt={alt} fallback={fallbackImage} />
            </a>
          </div>
          <div className={`pdp-image-thumb back-image ${isBack}`}>
            <a>
              <Image src={backImage} alt={alt} fallback={fallbackImage} />
            </a>
          </div>
        </span>
        {showPreview && (previewComponent)}
      </div>
    );
  };

  render() {
    const { bookFlip, frontImage, productType = 'Paperback',  backImage, inlayImage, previewComponent, alt, fallbackImage } = this.props;
    const bookOrientation = this.state.showBack ? 'show-back' : 'show-front';
    const showSide = this.state.showSide ? 'show-side' : '';
    const carrouselStyle = { '--book-depth': `${this.state.productThickness}px` }; // CSS variable (Custom property)
    const bookflipNav = this.bookFlipNav; // Store custom nav html in constant
    const imagesArray = [frontImage, backImage, inlayImage].filter(Boolean);
    const carrouselSettings = {
      dots: true,
      customPaging(i) {
        // Custom navigation if Bookflip boolean (BE) is set to true and images are available
        if (bookFlip && i === 0) {
          return bookflipNav(false);
        }
        return (
          <div className="flex-dots">
            <div className={`pdp-image-thumb`}>
              <span><Image alt={alt} src={imagesArray[i]} fallback={fallbackImage} /></span>
            </div>
            {i === (imagesArray.length - 1) && (previewComponent)}
          </div>
        );
      },

      infinite: false,
      speed: 400,
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: false,
      draggable: false,
      swipe: false,
      fade: true,
      variableWidth: false,
    };

    return (
      <div id="pdp-slider" className={`${bookFlip ? 'bookflip-slider' : ''} pdp-image-container`} style={carrouselStyle} ref={this.pdpslider}>
        <Slider
          ref={slider => (this.slider = slider)}
          {...carrouselSettings}
          className="product-slick-slider"
        >
          {/* Create a 3d cover when product has front and backcover and is of type "book" */}
          {bookFlip && (
            <div className="bookflip">
              <div
                id="pdp-product"
                className={`${bookOrientation} ${showSide} ${productType}`}
                ref={this.book}
                onClick={this.toggleFlip}
                onMouseOver={this.toggleSide}
                onMouseOut={this.toggleSide}
              >
                <div className="book-wrapper" ref={this.bookWrapper}>
                  <div className="bk-book book-1 bk-bookdefault book shadow-effect">
                    <div className="bk-front">
                      {/* Give the images a visibility hidden inline to prevent layout shifting */}
                      <Image src={frontImage} alt={alt} fallback={fallbackImage} style={{ visibility: 'hidden' }}/>
                    </div>
                    <div className="bk-back">
                      <Image src={backImage} alt={alt} fallback={fallbackImage} style={{ visibility: 'hidden' }}/>
                    </div>
                  </div>
                </div>
              </div>
              <span className="book-shadow"/>
            </div>
          )}
          {!bookFlip && imagesArray.map((val, i) => (
            <div key={i}>
              <Image src={val} alt={alt} fallback={fallbackImage} />
            </div>
          ))}
        </Slider>
        { /* Slick hides thumbnail navigation when there is only one slide.
              This renders the custom navigation when no additional images are present
              In this case only the preview button will be shown
          */  }
        {imagesArray.length < 2 ?
          (
            previewComponent
          ) : (
            !!bookFlip && (
              this.bookFlipNav()
            )
          )
        }
      </div>
    );
  }
}

ProductCarrousel.propTypes = {
  productWidth: PropTypes.number,
  productHeight: PropTypes.number,
  bookFlip: PropTypes.bool,
  productType: PropTypes.string,
  frontImage: PropTypes.string,
  backImage: PropTypes.string,
  inlayImage: PropTypes.string,
  imageShadow: PropTypes.bool,
  previewComponent: PropTypes.node,
  alt: PropTypes.string,
  fallbackImage: PropTypes.string,
};

export default ProductCarrousel;
